import type { DamageKind } from '@prism/sim';

/**
 * Floating damage numbers (TRD-04 §7).
 *
 * Pure data — the Pixi text objects are bound in `vfxRenderer.ts`, so the aggregation
 * rule below is unit-testable without a canvas.
 */

export type DamageNumberMode = 'all' | 'crits-only' | 'off';

export interface DamageNumber {
  active: boolean;
  /** Tile units. */
  x: number;
  y: number;
  vy: number;
  amount: number;
  crit: boolean;
  kind: DamageKind;
  life: number;
  /** Re-pops the scale when an existing number absorbs another hit. */
  pop: number;
  /** Which enemy this belongs to, so repeated hits can merge (R-04-20). */
  enemyId: number;
  lastHitAt: number;
}

export const MAX_DAMAGE_NUMBERS = 60;
const LIFETIME_MS = 700;
/** Hits on the same enemy inside this window merge instead of stacking (R-04-20). */
const AGGREGATE_WINDOW_MS = 120;

export const DAMAGE_COLORS: Record<DamageKind, number> = {
  physical: 0xffffff,
  magic: 0x9b5de5,
  true: 0xffd60a,
};
export const CRIT_COLOR = 0xffd60a;

function make(): DamageNumber {
  return {
    active: false,
    x: 0,
    y: 0,
    vy: 0,
    amount: 0,
    crit: false,
    kind: 'physical',
    life: 0,
    pop: 0,
    enemyId: -1,
    lastHitAt: 0,
  };
}

export class DamageNumbers {
  readonly items: DamageNumber[] = Array.from({ length: MAX_DAMAGE_NUMBERS }, make);
  mode: DamageNumberMode = 'all';

  private cursor = 0;
  private elapsed = 0;

  push(
    enemyId: number,
    x: number,
    y: number,
    amount: number,
    kind: DamageKind,
    crit: boolean,
  ): void {
    if (this.mode === 'off') return;
    if (this.mode === 'crits-only' && !crit) return;

    // Merge into a recent number on the same enemy rather than spawning a new one.
    // Without this a Tempest board — which lands several chained hits per shot per enemy —
    // buries the field under overlapping text and becomes completely unreadable.
    for (const item of this.items) {
      if (!item.active || item.enemyId !== enemyId) continue;
      if (this.elapsed - item.lastHitAt > AGGREGATE_WINDOW_MS) continue;
      item.amount += amount;
      item.crit = item.crit || crit;
      item.lastHitAt = this.elapsed;
      item.life = Math.max(item.life, LIFETIME_MS * 0.6);
      item.pop = 1;
      item.x = x;
      item.y = y;
      return;
    }

    const n = this.items[this.cursor] as DamageNumber;
    this.cursor = (this.cursor + 1) % MAX_DAMAGE_NUMBERS;

    n.active = true;
    n.x = x;
    n.y = y;
    n.vy = -0.9;
    n.amount = amount;
    n.crit = crit;
    n.kind = kind;
    n.life = LIFETIME_MS;
    n.pop = 1;
    n.enemyId = enemyId;
    n.lastHitAt = this.elapsed;
  }

  update(deltaMs: number): void {
    this.elapsed += deltaMs;
    const dt = deltaMs / 16.6667;

    for (const n of this.items) {
      if (!n.active) continue;
      n.life -= deltaMs;
      if (n.life <= 0) {
        n.active = false;
        continue;
      }
      n.vy += 0.05 * dt;
      n.y += n.vy * dt * 0.012;
      n.pop = Math.max(0, n.pop - deltaMs / 120);
    }
  }

  get live(): number {
    let n = 0;
    for (const item of this.items) if (item.active) n++;
    return n;
  }

  /** Test seam — aggregation is time-dependent, and tests should not sleep. */
  get now(): number {
    return this.elapsed;
  }

  clear(): void {
    for (const n of this.items) n.active = false;
  }
}
